import { FetchApi, IdentityApi } from '@backstage/core-plugin-api';
import { MetricsApi, MetricsApiOptions } from './MetricsApi';
import { DiscoveryApi } from '@backstage/core-plugin-api';
import {
  Metric,
  MetricDashboard,
  TopCatalogEntity,
  TopSearchTopic,
  TopTechDocsPage,
} from '@appfolio/backstage-plugin-metrics-common';
import { ResponseError } from '@backstage/errors';

interface MetricDashboardApiObject extends Omit<MetricDashboard, 'count'> {
  href: string;
  metrics: {
    href: string;
    count: number;
  };
}

const newUrl = (baseUrl: string, options: MetricsApiOptions) => {
  const { start, end, group, repo, enabledFlags } = options;
  const url = new URL(baseUrl);
  if (start) {
    url.searchParams.set('start', start);
  }
  if (end) {
    url.searchParams.set('end', end);
  }
  if (group) {
    url.searchParams.set('group', group);
  }
  if (repo) {
    url.searchParams.set('repo', repo);
  }
  if (enabledFlags?.length) {
    url.searchParams.set('enabledFlags', enabledFlags.join(','));
  }
  return url;
};

export class MetricsClient implements MetricsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly identityApi: IdentityApi;

  public constructor(deps: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    identityApi: IdentityApi;
  }) {
    const { discoveryApi, fetchApi, identityApi } = deps;
    this.discoveryApi = discoveryApi;
    this.fetchApi = fetchApi;
    this.identityApi = identityApi;
  }

  public async getDashboards(
    options?: MetricsApiOptions,
  ): Promise<MetricDashboard[]> {
    const baseUrl = await this.getBaseUrl();
    const url = newUrl(`${baseUrl}/dashboards`, options ?? {});
    const headers = await this.getHeaders();
    const response = await this.fetchApi.fetch(url.toString(), { headers });
    const results = (await response.json()) as MetricDashboardApiObject[];
    return results.map(({ href, metrics, ...rest }) => ({
      ...rest,
      count: metrics.count ?? 0,
    }));
  }

  public async getMetrics(
    dashboardId: string,
    options?: MetricsApiOptions,
  ): Promise<Metric[]> {
    const baseUrl = await this.getBaseUrl();
    const url = newUrl(
      `${baseUrl}/dashboards/${dashboardId}/metrics`,
      options ?? {},
    );
    const headers = await this.getHeaders();
    const response = await this.fetchApi.fetch(url.toString(), { headers });
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return await response.json();
  }

  public async getMetric(
    dashboardId: string,
    metricId: string,
    options?: MetricsApiOptions,
  ): Promise<Metric> {
    const baseUrl = await this.getBaseUrl();
    const url = newUrl(
      `${baseUrl}/dashboards/${dashboardId}/metrics/${metricId}`,
      options ?? {},
    );
    const headers = await this.getHeaders();
    const response = await this.fetchApi.fetch(url.toString(), { headers });
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return await response.json();
  }

  public async getTopTechDocsPages(
    limit: number,
    options?: { start?: string; end?: string },
  ): Promise<TopTechDocsPage[]> {
    const baseUrl = await this.getBaseUrl();
    const url = new URL(`${baseUrl}/docs/top-pages`);
    url.searchParams.set('limit', String(limit));
    if (options?.start) {
      url.searchParams.set('start', options.start);
    }
    if (options?.end) {
      url.searchParams.set('end', options.end);
    }
    const headers = await this.getHeaders();
    const response = await this.fetchApi.fetch(url.toString(), { headers });
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return await response.json();
  }

  public async getTopCatalogEntities(
    limit: number,
    options?: { start?: string; end?: string },
  ): Promise<TopCatalogEntity[]> {
    const baseUrl = await this.getBaseUrl();
    const url = new URL(`${baseUrl}/catalog/top-entities`);
    url.searchParams.set('limit', String(limit));
    if (options?.start) {
      url.searchParams.set('start', options.start);
    }
    if (options?.end) {
      url.searchParams.set('end', options.end);
    }
    const headers = await this.getHeaders();
    const response = await this.fetchApi.fetch(url.toString(), { headers });
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return await response.json();
  }

  public async getTopSearchTopics(): Promise<TopSearchTopic[]> {
    const baseUrl = await this.getBaseUrl();
    const url = new URL(`${baseUrl}/search/top-topics`);
    const headers = await this.getHeaders();
    const response = await this.fetchApi.fetch(url.toString(), { headers });
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return await response.json();
  }

  private async getBaseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('appfolio-metrics');
  }

  private async getHeaders(): Promise<Record<string, string>> {
    const { token } = await this.identityApi.getCredentials();
    return { Authorization: `Bearer ${token}` };
  }
}
